import type { SortingState } from "@tanstack/react-table"
import { fetchItemsAPI, type FetchItemsOptions } from "../../api/itemsApi"
import type { ArchivedListMode } from "@/utils/itemArchive"
import { buildTableSortParams } from "./buildTableRequestPayload"
import type { ServerTableColumnFilters } from "./buildServerColumnFilters"

export type FetchAllCollectionExportItemsInput = {
  slug: string
  organization?: string
  filters: Record<string, string[]>
  columnFilters?: ServerTableColumnFilters
  archived: ArchivedListMode
  sorting?: SortingState
}

/** Paginate through the collection list API with the same query as the table view. */
export async function fetchAllCollectionItemsForExport(
  input: FetchAllCollectionExportItemsInput
): Promise<Record<string, unknown>[]> {
  const sort = buildTableSortParams(input.sorting)
  // Must match GET /collections/:slug/items max page size for list (non-relate).
  const pageSize = 100
  const baseOptions: Omit<FetchItemsOptions, "page" | "limit"> = {
    ...(sort?.orderBy
      ? { orderBy: sort.orderBy, orderDirection: sort.orderDirection }
      : {}),
    filters: input.filters,
    columnFilters: input.columnFilters,
    ...(input.archived !== "no" ? { archived: input.archived } : {}),
    organization: input.organization?.trim() || undefined,
  }

  const all: Record<string, unknown>[] = []
  let page = 1
  for (; ;) {
    const { items, totalCount } = await fetchItemsAPI(input.slug, {
      ...baseOptions,
      page,
      limit: pageSize,
    })
    const batch = Array.isArray(items) ? items : []
    all.push(...(batch as Record<string, unknown>[]))
    if (batch.length === 0) break
    if (typeof totalCount === "number" && all.length >= totalCount) break
    if (batch.length < pageSize) break
    page += 1
  }
  return all
}
